/**
 * The planning page's sections, defined once: the page shows them one under
 * another, Present mode one at a time.
 */

import type { ReactElement, ReactNode } from 'react'
import { Caption1, MessageBar, MessageBarBody, Subtitle2, makeStyles, tokens } from '@fluentui/react-components'
import {
  CheckmarkCircle20Regular,
  ColumnTriple20Regular,
  Gavel20Regular,
  Lightbulb20Regular,
  Location20Regular,
  People20Regular,
  Target20Regular,
} from '@fluentui/react-icons'
import { useToast } from '../ToastProvider'
import { usePlanEdit } from '../../hooks/usePlanning'
import { apiErrorMessage } from '../../lib/apiError'
import { goalOf, ideaTarget, pickedLabel } from '../../lib/planning'
import type { PlanEdit, PlanSectionId, PlanningCycle } from '../../types/planning'
import { DecisionBoard } from './DecisionBoard'
import { IdeaBoard } from './IdeaBoard'
import { CommitTextarea, usePanelStyles } from './PlanParts'
import { PlanTimeline } from './PlanTimeline'
import { PlanWrapUp, RequestList } from './PlanWrapUp'
import { ScopeView } from './ScopeView'
import { WriteBackDialog } from './WriteBackDialog'

const NUMWORD = ['zero', 'one', 'two', 'three', 'four', 'five']

export interface PlanSectionDef {
  id: PlanSectionId
  /** Its name in the agenda. */
  nav: string
  icon: ReactElement
  title: string
  minutes?: number
  hint?: string
  draft?: boolean
  extra?: ReactNode
  body: ReactNode
}

const useStyles = makeStyles({
  // Versions and capacity as one row of tiles, read in one sweep (#1578).
  stats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
    gap: tokens.spacingHorizontalM,
  },
  stat: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  big: {
    fontSize: tokens.fontSizeHero700,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: 1.1,
    color: tokens.colorBrandForeground1,
  },
  goal: { fontSize: tokens.fontSizeBase400 },
  over: { color: tokens.colorPaletteRedForeground1 },
})

export function usePlanSections(data: PlanningCycle): PlanSectionDef[] {
  const styles = useStyles()
  const panel = usePanelStyles()
  const { showToast } = useToast()
  const { plan, state } = data
  const mutation = usePlanEdit(data.cycle)
  const edit = (e: PlanEdit) =>
    mutation.mutate(e, {
      onError: (err) =>
        showToast({ title: 'Not saved', body: apiErrorMessage(err), intent: 'error' }),
    })

  const min = plan.minutes ?? {}
  const picked = pickedLabel(plan, state)
  const shipped = plan.shipped?.items ?? []
  const stats = [
    ...(plan.versions ?? []).map((v) => ({ n: v.value, label: v.label })),
    ...(plan.capacity ?? []).map((c) => ({
      n: c.n,
      label: c.note ? `${c.label} (${c.note})` : c.label,
    })),
  ]

  const sections: (PlanSectionDef | false)[] = [
    {
      id: 'now',
      nav: 'Where we are',
      icon: <Location20Regular />,
      title: 'Where we are',
      minutes: min.now,
      body: (
        <>
          {stats.length > 0 && (
            <div className={styles.stats} data-testid="plan-stats">
              {stats.map((t) => (
                <div key={t.label} className={styles.stat}>
                  <span className={styles.big}>{t.n}</span>
                  <Caption1 className={panel.muted}>{t.label}</Caption1>
                </div>
              ))}
            </div>
          )}
          {shipped.length > 0 && (
            <div className={panel.panel}>
              <Caption1 className={panel.muted}>{plan.shipped?.label}</Caption1>
              <ul className={panel.list}>
                {shipped.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
            </div>
          )}
          {plan.timeline && <PlanTimeline timeline={plan.timeline} />}
        </>
      ),
    },
    {
      id: 'goal',
      nav: 'Goal',
      icon: <Target20Regular />,
      title: 'Cycle goal',
      minutes: min.goal,
      hint: plan.goal_hint,
      body: (
        <CommitTextarea
          className={styles.goal}
          aria-label="Cycle goal"
          placeholder="One sentence: what is true at the end of the cycle?"
          resize="vertical"
          rows={2}
          value={goalOf(plan, state)}
          onCommit={(v) => edit({ op: 'set', path: ['goal'], value: v })}
        />
      ),
    },
    {
      id: 'scope',
      nav: 'Scope',
      icon: <ColumnTriple20Regular />,
      title: 'Scope',
      minutes: min.scope,
      draft: true,
      hint: 'Drag a card to move it, or open it: move, owner and comments are there too. Anything new goes to Notes for later, not into this cycle.',
      body: <ScopeView plan={plan} state={state} edit={edit} cycle={data.cycle} />,
    },
    {
      id: 'decide',
      nav: 'Decisions',
      icon: <Gavel20Regular />,
      title: 'Decisions',
      minutes: min.decide,
      hint: "Open issues in this cycle labelled needs-discussion. Pick an option or type the outcome; it goes into the read-back.",
      body: <DecisionBoard plan={plan} state={state} edit={edit} cycle={data.cycle} />,
    },
    (plan.requests ?? []).length > 0 && {
      id: 'reqs',
      nav: 'Requests',
      icon: <People20Regular />,
      title: plan.requests_title || 'Requests',
      minutes: min.reqs,
      hint: 'Each request is mapped to where it would live. Decide per request; anything into this cycle names what leaves it.',
      body: <RequestList plan={plan} state={state} edit={edit} />,
    },
    (plan.ideas ?? []).length > 0 && {
      id: 'ideas',
      nav: 'Ideas',
      icon: <Lightbulb20Regular />,
      title: `Ideas: pick ${NUMWORD[ideaTarget(plan)] ?? ideaTarget(plan)} to try`,
      minutes: min.ideas,
      hint: 'Each idea is a GitHub issue with a two-week test. For each pick, agree who runs the test and what result counts as success.',
      extra: (
        <Subtitle2 aria-live="polite" className={picked.over ? styles.over : undefined}>
          {picked.text}
        </Subtitle2>
      ),
      body: (
        <>
          {plan.ideas_rule && (
            <MessageBar intent="warning">
              <MessageBarBody>{plan.ideas_rule}</MessageBarBody>
            </MessageBar>
          )}
          <IdeaBoard plan={plan} state={state} edit={edit} cycle={data.cycle} />
        </>
      ),
    },
    {
      id: 'wrap',
      nav: 'Wrap-up',
      icon: <CheckmarkCircle20Regular />,
      title: 'Wrap-up',
      minutes: min.wrap,
      hint: 'Read it back. Every item leaves the room with a name; red means a name is missing.',
      extra: <WriteBackDialog cycle={data.cycle} version={data.version} plan={plan} state={state} github={data.github} />,
      body: <PlanWrapUp plan={plan} state={state} />,
    },
  ]
  return sections.filter((s): s is PlanSectionDef => !!s)
}

/**
 * Bring an item into view and mark it for a moment: the card, question,
 * request or idea named by `target`, else the section's heading.
 */
export function spotlight(target: string | undefined, section: PlanSectionId): void {
  const find = (id: string) => document.querySelector<HTMLElement>(`[data-testid="${id}"]`)
  // A card is `card-<id>` in the columns and `map-card-<id>` or `map-band-<id>`
  // on the map, which is the default view (Copilot on #1666).
  const card = target?.startsWith('card-') ? target.slice('card-'.length) : undefined
  const el =
    (target ? find(target) : null) ??
    (card ? (find(`map-card-${card}`) ?? find(`map-band-${card}`)) : null) ??
    document.getElementById(`h-plan-${section}`)
  if (!el) return
  el.scrollIntoView?.({ block: 'center', behavior: 'smooth' })
  el.focus({ preventScroll: true })
  if (typeof el.animate !== 'function') return
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return
  el.animate(
    [
      { boxShadow: `0 0 0 3px ${tokens.colorBrandStroke1}` },
      { boxShadow: '0 0 0 3px transparent' },
    ],
    { duration: 1800, easing: 'ease-out' },
  )
}
